import * as React from 'react'
import { ReactElement, useEffect, useState } from 'react'
import { SelectableOptionMenuItemType } from '@fluentui/react'
import BaseDropdown, { ComboBoxOption } from './BaseDropdown'
import { formatDocumentNodeName } from '../../helpers/documentHelper'
import {
    DocumentOverviewItemDto,
    IFolderOverviewItemDto,
    IOverviewItemDto,
    IRootOverviewItemDto,
    isFolder,
} from '../../config/documentConfig'
import { DropdownConnectProps } from './Connect/DropdownConnect'
import { useTranslation } from 'react-i18next'

export enum DocumentSelectionType {
    // noinspection JSUnusedGlobalSymbols
    FolderOnly = 'FOLDER_ONLY',
    DocumentOnly = 'DOCUMENT_ONLY',
    Both = 'BOTH',
}

type Props = DropdownConnectProps<IOverviewItemDto> & {
    data: DocumentOverviewItemDto
    // Folders on the top level that should be excluded.
    exclude?: string[]
    selectionType?: DocumentSelectionType
    includeRoot?: boolean
}

function mapDocumentNode<R>(
    node: DocumentOverviewItemDto,
    cb: (node: DocumentOverviewItemDto, indent: number) => R,
    includeEmptyFolders: boolean = false,
    includeRoot: boolean = false,
    sort: (a: DocumentOverviewItemDto, b: DocumentOverviewItemDto) => number = () => 0,
    indent: number = 0,
): R[] {
    // Set the stack and parent ID, because it is not provided by the endpoint.
    const _node: DocumentOverviewItemDto = JSON.parse(JSON.stringify(node))
    if (_node.hasOwnProperty('children')) {
        let children: R[] = []
        if ((_node as IFolderOverviewItemDto).children instanceof Array) {
            children = (_node as IFolderOverviewItemDto).children
                .sort((a: DocumentOverviewItemDto, b: DocumentOverviewItemDto) => {
                    // Check if both are either folder or documents by using an XOR operator.
                    if (isFolder(a) !== isFolder(b)) {
                        // Folders are always arranged on top.
                        return isFolder(a) ? -1 : 1
                    }

                    // If 'a' is a folder, then b is also a folder, because of the check above.
                    if (isFolder(a)) {
                        // Folders are always sorted alphabetically ascending with preservation of numerical prefixes.
                        return a.name.toLowerCase().localeCompare(b.name.toLowerCase())
                    }

                    // Apply custom sorting when both items are documents.
                    return sort(a, b)
                })
                .flatMap((child: DocumentOverviewItemDto) =>
                    mapDocumentNode(child, cb, includeEmptyFolders, includeRoot, sort, indent + 1),
                )
        }

        // Only parse When the _node when it is a folder and has more than 0 children. So we don't include empty folders.
        // Construct a new array of items that is extended with the subset.
        if (!includeRoot && (node.stack || []).length === 0) {
            return children
        } else if (!includeEmptyFolders && children.length === 0) {
            return []
        } else {
            return [cb(_node, includeRoot ? indent : indent - 1), ...children]
        }
    }

    // The _node is a document.
    return [cb(_node, includeRoot ? indent : indent - 1)]
}

const mapFolders = (nodes: DocumentOverviewItemDto[] = []): IFolderOverviewItemDto[] =>
    // Only process nodes that have children.
    (nodes || []).filter(isFolder).map((node: IFolderOverviewItemDto) => ({
        ...node,
        children: mapFolders(node.children),
    }))

const mapDocuments = (props: Props) => {
    const selectionType = props.selectionType ?? DocumentSelectionType.DocumentOnly
    return mapDocumentNode<ComboBoxOption>(
        {
            ...(props.data as IRootOverviewItemDto),
            children:
                selectionType === DocumentSelectionType.FolderOnly
                    ? mapFolders((props.data as IRootOverviewItemDto).children)
                    : (props.data as IRootOverviewItemDto).children,
        },
        (node: DocumentOverviewItemDto, indent: number) => ({
            key: node.id,
            text: formatDocumentNodeName(node.name, node.type),
            // Make folder names a header, if only documents are allowed to be selected.
            itemType:
                selectionType === DocumentSelectionType.DocumentOnly && isFolder(node)
                    ? SelectableOptionMenuItemType.Header
                    : SelectableOptionMenuItemType.Normal,
            disabled: (props.exclude || []).includes(node.id),
            data: node,
            styles: { optionTextWrapper: { marginLeft: `${indent}em` } },
        }),
        selectionType === DocumentSelectionType.FolderOnly,
        props.includeRoot,
    )
}

export default function DocumentDropdown(props: Props): ReactElement {
    const [options, setOptions] = useState<ComboBoxOption<IOverviewItemDto>[]>([])
    const { t: translate } = useTranslation()

    useEffect(() => {
        setOptions(props.data ? mapDocuments(props) : undefined)
    }, [props.data])

    return (
        <BaseDropdown<DocumentOverviewItemDto>
            label={translate('base.documentdropdown.label')}
            placeholder={translate('base.documentdropdown.placeholder')}
            {...props}
            options={options}
        />
    )
}
